/**
 * Wikidata SPARQL utility.
 * Fetches entity info (label, description, image, aliases) and all properties
 * for a given Wikidata Q-id via the public SPARQL endpoint.
 */

const SPARQL_ENDPOINT = 'https://query.wikidata.org/sparql';

const WIKIDATA_ENTITY_ID_RE = /\/entity\/(Q\d+)$/;

function escapeSparqlString(value) {
    return String(value ?? '')
        .replace(/\\/g, '\\\\')
        .replace(/"/g, '\\"')
        .replace(/\r/g, ' ')
        .replace(/\n/g, ' ')
        .trim();
}

function normalizeWikidataImageUrl(url) {
    if (!url) return null;
    return url.replace(/^http:\/\//i, 'https://');
}

function extractWikidataId(entityUrl) {
    if (!entityUrl) return null;
    const match = entityUrl.match(WIKIDATA_ENTITY_ID_RE);
    return match?.[1] || null;
}

/**
 * True when Wikidata returned a real English label (not the Q-id fallback).
 * The label service emits the entity id when no rdfs:label exists for the language.
 */
function hasMeaningfulWikidataLabel(id, label) {
    if (!id || !label) return false;
    return label.trim().toUpperCase() !== id.trim().toUpperCase();
}

function buildRelatedCountryDetails(rows) {
    const countryMap = new Map();

    for (const row of rows || []) {
        const countryName = row.countryLabel?.value || null;
        if (!countryName) continue;

        const normalizedFlag = normalizeWikidataImageUrl(row.countryFlag?.value || null) || '';
        const existing = countryMap.get(countryName);

        countryMap.set(countryName, {
            name: countryName,
            flagUrl: normalizedFlag || existing?.flagUrl || '',
        });
    }

    return Array.from(countryMap.values()).sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * Run a SPARQL query against Wikidata and return parsed JSON bindings.
 */
async function runSparql(query) {
    const url = `${SPARQL_ENDPOINT}?query=${encodeURIComponent(query)}&format=json`;
    const res = await fetch(url, {
        headers: {'Accept': 'application/sparql-results+json'},
    });
    if (!res.ok) throw new Error(`SPARQL request failed: ${res.status}`);
    const json = await res.json();
    return json.results.bindings;
}

/**
 * Search for Wikidata entities via the SPARQL endpoint using the mwapi EntitySearch service.
 * Returns lightweight result rows enriched with image URLs when available.
 *
 * @param {string} query
 * @param {number} [limit=10]
 * @returns {Promise<Array<{id: string, label: string, description: string|null, imageUrl: string|null, url: string|null, relatedCountries: string[], relatedCountryDetails: Array<{name: string, flagUrl: string}>}>>}
 */
export async function searchWikidataEntities(query, limit = 10) {
    const trimmedQuery = query?.trim();
    if (!trimmedQuery) return [];

    const safeLimit = Math.max(1, Math.min(Number(limit) || 10, 10));
    const escapedQuery = escapeSparqlString(trimmedQuery);

    // GROUP BY + SAMPLE: optional P18 (image) multiplies rows per entity; DISTINCT does not collapse them.
    const searchQuery = `
        SELECT ?entity ?entityLabel (SAMPLE(?entityDescription) AS ?entityDescription) (SAMPLE(?image) AS ?image) ?ordinal WHERE {
            SERVICE wikibase:mwapi {
                bd:serviceParam wikibase:endpoint "www.wikidata.org";
                                wikibase:api "EntitySearch";
                                mwapi:search "${escapedQuery}";
                                mwapi:language "en";
                                mwapi:limit "${safeLimit}".
                ?entity wikibase:apiOutputItem mwapi:item .
                ?ordinal wikibase:apiOrdinal true .
            }
            OPTIONAL { ?entity schema:description ?entityDescription . FILTER(LANG(?entityDescription) = "en") }
            OPTIONAL { ?entity wdt:P18 ?image . }
            ?entity rdfs:label ?entityLabel .
            FILTER(LANG(?entityLabel) = "en")
        }
        GROUP BY ?entity ?entityLabel ?ordinal
        ORDER BY ?ordinal
        LIMIT ${safeLimit}
    `;

    const rows = await runSparql(searchQuery);

    const seenIds = new Set();
    return rows
        .map((row) => {
            const id = extractWikidataId(row.entity?.value);
            if (!id) return null;

            if (seenIds.has(id)) return null;
            seenIds.add(id);

            const label = row.entityLabel?.value || null;
            if (!hasMeaningfulWikidataLabel(id, label)) return null;

            return {
                id,
                label,
                description: row.entityDescription?.value || null,
                imageUrl: normalizeWikidataImageUrl(row.image?.value || null),
                url: `https://www.wikidata.org/wiki/${id}`,
                relatedCountries: [],
                relatedCountryDetails: [],
            };
        })
        .filter(Boolean);
}

/**
 * Fetch rich entity details via SPARQL, including related countries derived from a
 * focused set of direct-country and place->country relationships.
 *
 * @param {string} qid
 * @returns {Promise<{id: string, label: string, description: string|null, imageUrl: string|null, url: string, relatedCountries: string[], relatedCountryDetails: Array<{name: string, flagUrl: string}>}|null>}
 */
export async function fetchWikidataEntityDetails(qid) {
    if (!qid) return null;

    const detailsQuery = `
        SELECT ?label ?description ?image ?countryLabel ?countryFlag WHERE {
            BIND(wd:${escapeSparqlString(qid)} AS ?entity)

            OPTIONAL { ?entity rdfs:label ?label . FILTER(LANG(?label) = "en") }
            OPTIONAL { ?entity schema:description ?description . FILTER(LANG(?description) = "en") }
            OPTIONAL { ?entity wdt:P18 ?image . }

            OPTIONAL {
                {
                    VALUES ?countryProp { wdt:P17 wdt:P27 wdt:P495 wdt:P1001 wdt:P1532 }
                    ?entity ?countryProp ?country .
                    ?country wdt:P31/wdt:P279* wd:Q6256 .
                }
                UNION
                {
                    VALUES ?placeProp { wdt:P19 wdt:P20 wdt:P159 wdt:P551 wdt:P740 wdt:P937 }
                    ?entity ?placeProp ?place .
                    ?place wdt:P17 ?country .
                    ?country wdt:P31/wdt:P279* wd:Q6256 .
                }

                OPTIONAL { ?country wdt:P41 ?countryFlag . }

                SERVICE wikibase:label { bd:serviceParam wikibase:language "en". }
            }
        }
        LIMIT 50
    `;

    const rows = await runSparql(detailsQuery);
    if (!rows.length) return null;

    const firstRow = rows[0];
    const relatedCountryDetails = buildRelatedCountryDetails(rows);
    // Country UNION rows can omit rdfs:label; scan all bindings for a real English label.
    let label = null;
    for (const row of rows) {
        const value = row?.label?.value;
        if (typeof value === 'string' && value.trim() && !/^Q\d+$/i.test(value.trim())) {
            label = value.trim();
            break;
        }
    }
    if (!label) {
        const fallback = firstRow.label?.value;
        label = (typeof fallback === 'string' && fallback.trim()) ? fallback.trim() : qid;
    }

    return {
        id: qid,
        label,
        description: firstRow.description?.value || null,
        imageUrl: normalizeWikidataImageUrl(firstRow.image?.value || null),
        url: `https://www.wikidata.org/wiki/${qid}`,
        relatedCountries: relatedCountryDetails.map((item) => item.name),
        relatedCountryDetails,
    };
}

/**
 * Fetch basic entity info: label, description, image URL, aliases, instance-of types.
 * @param {string} qid - e.g. "Q42"
 * @returns {Promise<Object|null>}
 */
export async function fetchWikidataEntityInfo(qid) {
    if (!qid) return null;

    // Basic info query
    const infoQuery = `
        SELECT ?label ?description ?image ?instanceOfLabel WHERE {
            OPTIONAL { wd:${qid} rdfs:label ?label . FILTER(LANG(?label) = "en") }
            OPTIONAL { wd:${qid} schema:description ?description . FILTER(LANG(?description) = "en") }
            OPTIONAL { wd:${qid} wdt:P18 ?image . }
            OPTIONAL { wd:${qid} wdt:P31 ?instanceOf . ?instanceOf rdfs:label ?instanceOfLabel . FILTER(LANG(?instanceOfLabel) = "en") }
        } LIMIT 20
    `;

    const infoRows = await runSparql(infoQuery);

    if (!infoRows.length) return null;

    const label = infoRows[0]?.label?.value || qid;
    const description = infoRows[0]?.description?.value || '';
    const image = infoRows[0]?.image?.value || null;
    const instanceOfLabels = [...new Set(infoRows.map(r => r.instanceOfLabel?.value).filter(Boolean))];

    // Aliases query
    const aliasQuery = `
        SELECT ?alias WHERE {
            wd:${qid} skos:altLabel ?alias .
            FILTER(LANG(?alias) = "en")
        }
    `;
    let aliases = [];
    try {
        const aliasRows = await runSparql(aliasQuery);
        aliases = aliasRows.map(r => r.alias?.value).filter(Boolean);
    } catch {
        // aliases are optional
    }

    return {
        wikidataId: qid,
        canonicalName: label,
        description,
        image: normalizeWikidataImageUrl(image),
        aliases,
        entityType: instanceOfLabels[0] || null,
        instanceOfLabels,
    };
}

/**
 * Fetch all properties (claims) of a Wikidata entity as label→value pairs.
 * Returns an array of { property, propertyLabel, value, valueLabel }.
 * @param {string} qid
 * @returns {Promise<Array>}
 */
export async function fetchWikidataProperties(qid) {
    if (!qid) return [];

    const query = `
        SELECT ?prop ?propLabel ?val ?valLabel WHERE {
            wd:${qid} ?p ?statement .
            ?statement ?ps ?val .
            ?prop wikibase:claim ?p .
            ?prop wikibase:statementProperty ?ps .
            SERVICE wikibase:label { bd:serviceParam wikibase:language "en". }
        }
        LIMIT 200
    `;

    const rows = await runSparql(query);

    return rows.map(r => ({
        property: r.prop?.value || '',
        propertyLabel: r.propLabel?.value || '',
        value: r.val?.value || '',
        valueLabel: r.valLabel?.value || '',
    }));
}

